import React, { useState } from 'react';
import { CONSTANTS } from '../../constants';

const WeatherReport = ({ onClose }) => {
  const [queryType, setQueryType] = useState(0);
  const [weatherQuery, setWeatherQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [error, setError] = useState('');

  const queryTypes = [
    { value: 0, name: 'City' },
    { value: 1, name: 'Zip Code' }
  ];

  const validateInput = () => {
    if (queryType === 0) {
      if (!isNaN(parseFloat(weatherQuery)) && !isNaN(Number(weatherQuery) - 0)) {
        setError('Please enter a valid city name.');
        return false;
      }
    }

    if (queryType === 1) {
      const isValidZip = /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(weatherQuery);
      if (!isValidZip) {
        setError('Please enter a valid 5 digit Zip Code.');
        return false;
      }
    }
    return true;
  };

  const searchWeatherReport = async () => {
    if (!validateInput()) {
      return;
    }

    setSearching(true);
    setError('');

    const queryParams: any = {
      APPID: CONSTANTS.WEATHER.API_KEY
    };

    if (queryType === 0) {
      queryParams.q = weatherQuery + ',us';
    } else {
      queryParams.zip = weatherQuery + ',us';
    }

    try {
      const url = new URL(CONSTANTS.WEATHER.ENDPOINT);
      Object.keys(queryParams).forEach(key => url.searchParams.append(key, queryParams[key]));
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setWeatherData(convertData(data));
    } catch (error) {
      setError(`There was an error getting weather information for ${weatherQuery}. Please wait 10 minutes and try again. The server detected the error was: ${error.message}`);
    } finally {
      setSearching(false);
    }
  };

  const convertData = (data) => {
    const converted = { ...data };
    converted.main.temp = kelvinToFahrenheit(data.main.temp);
    converted.main.temp_min = kelvinToFahrenheit(data.main.temp_min);
    converted.main.temp_max = kelvinToFahrenheit(data.main.temp_max);
    converted.main.humidity = (data.main.humidity / 100);
    return converted;
  };

  const kelvinToFahrenheit = (kelvin) => {
    return kelvin * (9 / 5) - 459.67;
  };

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="queryType" className="form-label">Search Type:</label>
        <select
          className="form-select"
          id="queryType"
          value={queryType}
          onChange={(e) => setQueryType(parseInt(e.target.value))}
        >
          {queryTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label htmlFor="weatherQuery" className="form-label">
          {queryType === 0 ? 'City Name:' : 'Zip Code:'}
        </label>
        <input
          type="text"
          className="form-control"
          id="weatherQuery"
          value={weatherQuery}
          onChange={(e) => setWeatherQuery(e.target.value)}
          placeholder={queryType === 0 ? 'Enter city name' : 'Enter zip code'}
        />
      </div>

      <button 
        className="btn btn-primary"
        onClick={searchWeatherReport}
        disabled={searching || !weatherQuery.trim()}
      >
        {searching ? 'Searching...' : 'Get Weather'}
      </button>

      {error && (
        <div className="alert alert-danger mt-3">
          {error}
        </div>
      )}

      {weatherData && (
        <div className="mt-4">
          <div className="card">
            <div className="card-header">
              <h5>Weather in {weatherData.name}</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Temperature:</strong> {Math.round(weatherData.main.temp)}°F</p>
                  <p><strong>Min Temp:</strong> {Math.round(weatherData.main.temp_min)}°F</p>
                  <p><strong>Max Temp:</strong> {Math.round(weatherData.main.temp_max)}°F</p>
                  <p><strong>Feels Like:</strong> {Math.round(weatherData.main.feels_like * (9/5) - 459.67)}°F</p>
                </div>
                <div className="col-md-6">
                  <p><strong>Humidity:</strong> {Math.round(weatherData.main.humidity * 100)}%</p>
                  <p><strong>Pressure:</strong> {weatherData.main.pressure} hPa</p>
                  <p><strong>Weather:</strong> {weatherData.weather[0].description}</p>
                  <p><strong>Wind Speed:</strong> {weatherData.wind?.speed || 'N/A'} m/s</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherReport;
